import {
	useGetAreas,
	useGetParcellingAccessible,
	useGetProperties,
	useGetSectors,
	useGetSoilAnalyses,
} from '@practium/features/api';
import type { FormSelectOption } from '@practium/ui';
import { FormField, FormInput, FormSectionTitle, FormSelect } from '@practium/ui';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import {
	useGetAdviceCorrection,
	useGetAdviceCorrectionById,
} from '../../../../api/hooks/recommendations';
import {
	CROP_STAGE_OPTIONS,
	formatPeriodLabel,
	PARCELLING_TYPE_LABELS,
	PARCELLING_TYPE_OPTIONS,
} from '../../../constants/maintenance.constants';
import type { UseMaintenanceFormOptions } from '../../../hooks/useMaintenanceForm';
import { useMaintenanceForm } from '../../../hooks/useMaintenanceForm';
import { useMaintenanceParcelling } from '../../../hooks/useMaintenanceParcelling';
import type {
	FertilizationPlanAdvice,
	MaintenanceParcellingType,
	RecommendationPageMode,
} from '../../../types';
import {
	getMaintenanceCycleStartLabel,
	getMaintenanceParcellingType,
	getMaintenanceReferenceId,
	getMaintenanceReferenceName,
	getMaintenanceSectorNames,
	normalizeMaintenanceMonth,
} from '../../../utils/maintenanceAdviceMapper';
import { MonthPicker } from '../../MonthPicker/MonthPicker';
import { MultiSelect } from '../../MultiSelect/MultiSelect';
import { ParcellingPreview } from '../../ParcellingPreview/ParcellingPreview';
import styles from './MaintenanceForm.module.css';

const NONE_VALUE = 'none';

export interface MaintenanceFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

export interface MaintenanceFormProps extends UseMaintenanceFormOptions {
	mode?: RecommendationPageMode;
	viewData?: FertilizationPlanAdvice;
	onLoadingChange?: (loading: boolean) => void;
	adviceId?: string;
}

const includeCurrentOption = (
	options: FormSelectOption[],
	value: FertilizationPlanAdvice['prop'] | null | undefined,
	fallbackLabel = ''
) => {
	const id = getMaintenanceReferenceId(value);
	if (!id || options.some((option) => option.value === id)) return options;
	return [
		{ value: id, label: getMaintenanceReferenceName(value, fallbackLabel) || id },
		...options,
	];
};

export const MaintenanceForm = forwardRef<MaintenanceFormRef, MaintenanceFormProps>(
	function MaintenanceForm(
		{
			mode = 'add',
			viewData,
			onLoadingChange,
			...hookProps
		},
		ref
	) {
		const isEdit = mode === 'edit';
		const isView = mode === 'view';
		const shouldLoadOptions = !isView && !isEdit;
		const plantingAdviceId = getMaintenanceReferenceId(viewData?.plantingAdvice);
		const embeddedPlantingAdviceName =
			viewData?.plantingAdviceName ||
			(viewData?.plantingAdvice && typeof viewData.plantingAdvice === 'object'
				? getMaintenanceReferenceName(viewData.plantingAdvice)
				: '');
		const { data: resolvedPlantingAdvice, isLoading: isLoadingPlantingAdvice } =
			useGetAdviceCorrectionById(plantingAdviceId, {
				enabled: !!plantingAdviceId && !embeddedPlantingAdviceName,
			});
		const resolvedPlantingAdviceName =
			embeddedPlantingAdviceName ||
			resolvedPlantingAdvice?.name ||
			(isLoadingPlantingAdvice ? 'Carregando...' : '');
		const { form, errors, isLoading, set, handleSubmit, isDirty, setParcellingContext } =
			useMaintenanceForm({
				...hookProps,
				validationMode: isEdit ? 'identification' : 'full',
			});
		const { data: properties = [] } = useGetProperties({ limit: 300, enabled: shouldLoadOptions });
		const { data: areas = [] } = useGetAreas({ limit: 300, enabled: shouldLoadOptions });
		const { data: sectors = [] } = useGetSectors({
			limit: 300,
			areaId: form.area || undefined,
			enabled: shouldLoadOptions && !!form.area,
		});
		const { data: soilAnalyses = [] } = useGetSoilAnalyses({
			areaId: form.area,
			limit: 300,
			enabled: shouldLoadOptions && !!form.area,
		});
		const { data: correctionAdvices = [] } = useGetAdviceCorrection({
			limit: 300,
			areaId: form.area || undefined,
			enabled: shouldLoadOptions && !!form.area,
		});
		const selectedParcellingType = (form.parcellingType || undefined) as
			| MaintenanceParcellingType
			| undefined;
		const { data: parcellings = [] } = useGetParcellingAccessible({
			limit: 300,
			enabled: shouldLoadOptions,
			typeParcelling: selectedParcellingType ?? 'fixed',
		});
		const {
			cycleStartOptions,
			previewRows,
			showParcellingFields,
			showCycleStartField,
			showParcellingPreview,
			resetParcellingFields,
			setParcellingType,
			setParcelling,
		} = useMaintenanceParcelling({
			form,
			set,
			viewData,
			isView,
			isEdit,
			onContextChange: setParcellingContext,
		});

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange?.(isLoading);
		}, [isLoading, onLoadingChange]);

		const areaRecords = areas.filter((area) => area.propId === form.prop);
		const fetchedPropOptions = useMemo(
			() => properties.map((property) => ({ value: property._id, label: property.name })),
			[properties]
		);
		const fetchedAreaOptions = areaRecords.map((area) => ({
			value: area._id ?? '',
			label: area.name,
		}));
		const firstSelectedSector = sectors.find((sector) => sector._id === form.sectors[0]);
		const selectedSpacing = firstSelectedSector
			? `${firstSelectedSector.lineSpacing}x${firstSelectedSector.plantSpacing}`
			: '';
		const fetchedSectorOptions: FormSelectOption[] = sectors
			.filter((sector) => {
				const sectorAreaId =
					getMaintenanceReferenceId(sector.areaId) ||
					getMaintenanceReferenceId(
						(sector as { area?: FertilizationPlanAdvice['area'] }).area
					);
				return !sectorAreaId || sectorAreaId === form.area;
			})
			.map((sector) => ({
				value: sector._id,
				label: `${sector.name} (${sector.lineSpacing} x ${sector.plantSpacing})`,
				disabled:
					!!selectedSpacing && `${sector.lineSpacing}x${sector.plantSpacing}` !== selectedSpacing,
			}));
		const fetchedSoilAnalysisOptions = soilAnalyses.map((analysis) => ({
			value: analysis._id,
			label: analysis.name,
		}));
		const areaCorrectionOptions = correctionAdvices
			.filter((advice) => !form.area || getMaintenanceReferenceId(advice.area) === form.area)
			.map((advice) => ({ value: advice._id, label: advice.name }));
		const fetchedPlantingAdviceOptions = [
			{ value: NONE_VALUE, label: 'Não' },
			...areaCorrectionOptions,
		];
		const canSelectCorrection = !isEdit && !!form.area && areaCorrectionOptions.length > 0;
		const fetchedParcellingOptions = parcellings.map((parcelling) => ({
			value: parcelling._id,
			label: parcelling.name,
		}));
		const activePropOptions = includeCurrentOption(
			fetchedPropOptions,
			viewData?.prop,
			viewData?.propName
		);
		const activeAreaOptions = includeCurrentOption(
			fetchedAreaOptions,
			viewData?.area,
			viewData?.areaName
		);
		const activeSoilAnalysisOptions = includeCurrentOption(
			fetchedSoilAnalysisOptions,
			viewData?.soilAnalysis,
			viewData?.soilAnalysisName
		);
		const activePlantingAdviceOptions = includeCurrentOption(
			fetchedPlantingAdviceOptions,
			viewData?.plantingAdvice,
			resolvedPlantingAdviceName
		);
		const activeParcellingOptions = includeCurrentOption(
			fetchedParcellingOptions,
			viewData?.parcelling,
			viewData?.parcellingName
		);
		const activeSectorOptions = (viewData?.sectors ?? []).reduce<FormSelectOption[]>(
			(options, sector) => includeCurrentOption(options, sector),
			fetchedSectorOptions
		);
		if (isView && viewData) {
			const propName = getMaintenanceReferenceName(viewData.prop, viewData.propName);
			const areaName = getMaintenanceReferenceName(viewData.area, viewData.areaName);
			const sectorNames = getMaintenanceSectorNames(viewData.sectors);
			const soilAnalysisName = getMaintenanceReferenceName(
				viewData.soilAnalysis,
				viewData.soilAnalysisName
			);
			const plantingAdviceName = resolvedPlantingAdviceName;
			const parcellingName = getMaintenanceReferenceName(
				viewData.parcelling,
				viewData.parcellingName
			);
			const parcellingType = getMaintenanceParcellingType(viewData);
			const cycleStartLabel = getMaintenanceCycleStartLabel(viewData);
			const floweringMonth =
				normalizeMaintenanceMonth(viewData.floweringMonth) ||
				normalizeMaintenanceMonth(viewData.floweringDate);

			return (
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Identificação</FormSectionTitle>
						<div className={styles.sectionFields}>
							<div className={styles.row2}>
								<FormField label="Identificação">
									<span className={styles.viewText}>{viewData.name || '—'}</span>
								</FormField>
								<FormField label="Propriedade">
									<span className={styles.viewText}>{propName || '—'}</span>
								</FormField>
							</div>
							<div className={styles.row2}>
								<FormField label="Área">
									<span className={styles.viewText}>{areaName || '—'}</span>
								</FormField>
								<FormField label="Setores">
									<span className={styles.viewText}>{sectorNames.join(', ') || '—'}</span>
								</FormField>
							</div>
							<div className={styles.row2}>
								<FormField label="Análises de solo da área">
									<span className={styles.viewText}>{soilAnalysisName || '—'}</span>
								</FormField>
								<FormField label="Usar adubação de correção">
									<span className={styles.viewText}>
										{getMaintenanceReferenceId(viewData.plantingAdvice)
											? plantingAdviceName || 'Sim'
											: 'Não'}
									</span>
								</FormField>
							</div>
							<div className={styles.row2}>
								<FormField label="Fase da lavoura">
									<span className={styles.viewText}>
										{CROP_STAGE_OPTIONS.find((o) => o.value === viewData.cropStage)?.label ?? '—'}
									</span>
								</FormField>
								<FormField label="Produtividade esperada">
									<span className={styles.viewText}>{viewData.productivity ?? '—'}</span>
								</FormField>
							</div>
						</div>
					</div>

					<div className={styles.section}>
						<FormSectionTitle>Parcelamento</FormSectionTitle>
						<div className={styles.sectionFields}>
							<div className={styles.row2}>
								<FormField label="Esquema de parcelamento">
									<span className={styles.viewText}>
										{parcellingType ? PARCELLING_TYPE_LABELS[parcellingType] : '—'}
									</span>
								</FormField>
								<FormField label="Parcelamento">
									<span className={styles.viewText}>{parcellingName || '—'}</span>
								</FormField>
							</div>
							<div className={styles.columns2}>
								{parcellingType === 'fixed' && (
									<FormField label="Mês do início do ciclo">
										<span className={styles.viewText}>{cycleStartLabel || '—'}</span>
									</FormField>
								)}
								<FormField label="Mês da florada">
									<span className={styles.viewText}>{formatPeriodLabel(floweringMonth) || '—'}</span>
								</FormField>
							</div>
							{showParcellingPreview && (
								<ParcellingPreview rows={previewRows} />
							)}
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.form}>
				<div className={styles.section}>
					<FormSectionTitle>Identificação</FormSectionTitle>
					<div className={styles.sectionFields}>
						<div className={styles.row2}>
							<FormField label="Identificação" required error={errors.name}>
								<FormInput
									placeholder="Ex.: Recomendação José - 2025/2026"
									value={form.name}
									onChange={(e) => set('name', e.target.value)}
									hasError={!!errors.name}
								/>
							</FormField>
							<FormField label="Propriedade" required={!isEdit} error={errors.prop}>
								<FormSelect
									value={form.prop}
									onChange={(v) => {
										set('prop', v);
										set('area', '');
										set('soilAnalysis', '');
										set('sectors', []);
										set('plantingAdvice', NONE_VALUE);
										resetParcellingFields();
									}}
									options={activePropOptions}
									placeholder="Selecione a propriedade"
									hasError={!!errors.prop}
									disabled={isEdit}
								/>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField label="Área" required={!isEdit} error={errors.area}>
								<FormSelect
									value={form.area}
									onChange={(v) => {
										set('area', v);
										set('soilAnalysis', '');
										set('sectors', []);
										set('plantingAdvice', NONE_VALUE);
										resetParcellingFields();
									}}
									options={activeAreaOptions}
									placeholder="Selecione a área"
									hasError={!!errors.area}
									disabled={isEdit}
								/>
							</FormField>
							<FormField label="Setores" required={!isEdit} error={errors.sectors}>
								<MultiSelect
									values={form.sectors}
									onChange={(v) => set('sectors', v)}
									options={activeSectorOptions}
									placeholder="Selecione os setores"
									hasError={!!errors.sectors}
									disabled={isEdit || !form.area}
								/>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField
								label="Análises de solo da área"
								required={!isEdit}
								error={errors.soilAnalysis}
							>
								<FormSelect
									value={form.soilAnalysis}
									onChange={(v) => set('soilAnalysis', v)}
									options={activeSoilAnalysisOptions}
									placeholder="Selecione a análise"
									hasError={!!errors.soilAnalysis}
									disabled={isEdit}
								/>
							</FormField>
							<FormField label="Usar adubação de correção" error={errors.plantingAdvice}>
								<FormSelect
									value={form.plantingAdvice || NONE_VALUE}
									onChange={(v) => set('plantingAdvice', v)}
									options={activePlantingAdviceOptions}
									placeholder="Não"
									hasError={!!errors.plantingAdvice}
									disabled={!canSelectCorrection}
								/>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField label="Fase da lavoura" required={!isEdit} error={errors.cropStage}>
								<FormSelect
									value={form.cropStage}
									onChange={(v) => {
										set('cropStage', v);
										resetParcellingFields();
										if (v === 'growth') set('productivity', '');
									}}
									options={CROP_STAGE_OPTIONS}
									placeholder="Selecione a fase"
									hasError={!!errors.cropStage}
									disabled={isEdit}
								/>
							</FormField>
							<FormField
								label="Produtividade esperada"
								error={errors.productivity}
							>
								<FormInput
									placeholder="Ex.: 1000"
									type="number"
									min="0"
									step="5"
									suffix="sc/ha"
									value={form.productivity}
									onChange={(e) => set('productivity', e.target.value)}
									hasError={!!errors.productivity}
									disabled={isEdit || form.cropStage === 'growth'}
								/>
							</FormField>
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Parcelamento</FormSectionTitle>
					<div className={styles.sectionFields}>
						<div className={styles.columns2}>
							<FormField
								label="Esquema de parcelamento"
								required={!isEdit}
								error={errors.parcellingType}
							>
								<FormSelect
									value={form.parcellingType}
									onChange={(v) => setParcellingType(v as MaintenanceParcellingType | '')}
									options={PARCELLING_TYPE_OPTIONS}
									placeholder="Selecione o esquema de parcelamento"
									hasError={!!errors.parcellingType}
									disabled={isEdit}
								/>
							</FormField>
							{showParcellingFields && (
								<FormField label="Parcelamento" required={!isEdit} error={errors.parcelling}>
									<div className={styles.reveal}>
										<FormSelect
											value={form.parcelling}
											onChange={setParcelling}
											options={activeParcellingOptions}
											placeholder="Selecione o parcelamento"
											hasError={!!errors.parcelling}
											disabled={isEdit}
										/>
									</div>
								</FormField>
							)}
						</div>
						{showParcellingFields && (
							<div className={`${styles.columns2} ${styles.reveal}`}>
								{showCycleStartField && (
									<FormField
										label="Mês do início do ciclo"
										required={!isEdit}
										error={errors.cycleStartMonth}
									>
										<FormSelect
											value={form.cycleStartMonth}
											onChange={(v) => set('cycleStartMonth', v)}
											options={cycleStartOptions}
											placeholder="Selecione o início do ciclo"
											hasError={!!errors.cycleStartMonth}
											disabled={isEdit}
										/>
									</FormField>
								)}
								<FormField label="Mês da florada" required={!isEdit} error={errors.floweringMonth}>
									<MonthPicker
										value={form.floweringMonth}
										onChange={(v) => set('floweringMonth', v)}
										placeholder="Selecione o mês da florada"
										hasError={!!errors.floweringMonth}
										disabled={isEdit}
									/>
								</FormField>
							</div>
						)}
						{showParcellingPreview && (
							<div className={styles.reveal}>
								<ParcellingPreview rows={previewRows} />
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
);
